import { useTenant } from '@emerald-works-nova/auth'
import { useSession } from '@emerald-works/react-auth'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { userSlice } from '../reducers'
import { isContentOnlyTenantLayout } from '../utils/contentOnlyLayout'
import useProducts from './useProducts'

/**
 * Hook to control the UserPreferences modal opening
 * Works for all authenticated users on any application route
 *
 * Rules:
 * - Mandatory tenants (features.lpq.isOptional === false): opens automatically if
 *   lpqCompleted = false, ignoring past opt-outs.
 * - Optional tenants (default): opens automatically only if not completed, not opted
 *   out (lpqDontShowAgain) and outside the 24h lpqClosedAt cooldown.
 * - Can be triggered manually via URL ?lpq parameter
 * - Can be triggered programmatically via openLPQ()
 */
export const useLPQ = () => {
  const location = useLocation()
  const profile = useSelector(userSlice.selectors.selectProfile)
  const { isAuthenticated } = useSession()

  const [shouldOpenLPQ, setShouldOpenLPQ] = useState(false)
  const [lpqParam, setLpqParam] = useState(null)
  const [hasProcessedLPQ, setHasProcessedLPQ] = useState(false)
  const [hasAutoOpened, setHasAutoOpened] = useState(false)
  const [hasBeenClosed, setHasBeenClosed] = useState(false)
  const tenant = useTenant()
  const { hasLPQAccess, showLPQModalPopup, lpqMandatory } = useProducts()

  // Check if current route is an authentication route
  const isAuthRoute = useCallback(() => {
    const authPaths = [
      '/login',
      '/signin',
      '/signup',
      '/admin/internal-login',
      '/password-recovery',
      '/password-recovered',
      '/password-reset-end',
      '/change-password',
      '/verify-email',
      '/social-signup/redirect'
    ]

    return authPaths.some(path => {
      if (path.includes(':')) {
        // Handle dynamic routes like /signup/:plan, /verify-email/:username/:code
        const pathSegments = path.split('/')
        const currentSegments = location.pathname.split('/')

        if (pathSegments.length !== currentSegments.length) return false

        return pathSegments.every((segment, index) => {
          return segment.startsWith(':') || segment === currentSegments[index]
        })
      }
      return location.pathname.startsWith(path)
    })
  }, [location.pathname])

  useEffect(() => {
    if (!isAuthenticated || !profile || isAuthRoute() || !tenant || isContentOnlyTenantLayout(tenant.tenantKey) || !hasLPQAccess) {
      setShouldOpenLPQ(false)
      setLpqParam(null)
      setHasProcessedLPQ(false)
      setHasAutoOpened(false)
      setHasBeenClosed(false)
      return
    }

    // Use window.location.search to check actual URL (React Router location may be stale after replaceState)
    const actualSearchParams = new URLSearchParams(window.location.search)
    const hasActualLpqParam = actualSearchParams.has('lpq')

    const searchParams = new URLSearchParams(location.search)
    const hasLpqParam = searchParams.has('lpq')

    // Early return if already processed and no new lpq param in actual URL
    if (hasProcessedLPQ && !hasActualLpqParam) {
      return
    }

    const shouldOpenBasedOnRules = () => {
      if (lpqMandatory) return !profile.lpqCompleted
      // optional → restore pre-e2b9a41a0 rules:
      if (profile.lpqCompleted || profile.lpqDontShowAgain) return false
      if (profile.lpqClosedAt) {
        const twentyFourHours = 24 * 60 * 60 * 1000
        if (new Date().valueOf() - profile.lpqClosedAt < twentyFourHours) return false
      }
      return true
    }

    if (hasLpqParam && !hasProcessedLPQ) {
      setLpqParam(hasLpqParam ? 'lpqParam' : 'manual')
      setShouldOpenLPQ(true)
      setHasProcessedLPQ(true)
      const newSearchParams = new URLSearchParams(location.search)
      newSearchParams.delete('lpq')
      const newSearch = newSearchParams.toString()
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`

      window.history.replaceState({}, '', newUrl)
    } else if (shouldOpenBasedOnRules() && !hasAutoOpened && !hasProcessedLPQ && !hasBeenClosed && showLPQModalPopup) {
      setLpqParam('auto')
      setShouldOpenLPQ(true)
      setHasAutoOpened(true)
    }

    if (!hasLpqParam && hasBeenClosed) {
      setHasBeenClosed(false)
    }
  }, [location.search, location.pathname, isAuthenticated, profile, hasProcessedLPQ, hasAutoOpened, hasBeenClosed, tenant, isAuthRoute, hasLPQAccess, showLPQModalPopup, lpqMandatory])

  const closeLPQ = () => {
    setShouldOpenLPQ(false)
    setLpqParam(null)
    setHasProcessedLPQ(true)
    setHasAutoOpened(false)
    setHasBeenClosed(true)
  }

  const openLPQ = () => {
    setShouldOpenLPQ(true)
    setLpqParam('manual')
    setHasProcessedLPQ(true)
    setHasBeenClosed(false)
  }

  return {
    shouldOpenLPQ,
    lpqParam,
    closeLPQ,
    openLPQ,
    isAuthenticated,
    isLPQOpen: shouldOpenLPQ
  }
}

export default useLPQ
